import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { AssetCard } from '../components/AssetCard';
import { SearchBar } from '../components/SearchBar';
import { FilterControls } from '../components/FilterControls';
import { MetricCard } from '../components/MetricCard';
import { SkeletonCard, PageLoader } from '../components/LoadingSpinner';
import { assetsAPI, portfolioAPI } from '../lib/api';
import { Truck, Container, TrendingUp, DollarSign, AlertTriangle } from 'lucide-react';
import { formatCurrency, formatNumber } from '../lib/formatters';
import { toast } from 'sonner';

export default function DashboardPage() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ type: '', status: '' });
  const [portfolio, setPortfolio] = useState(null);

  useEffect(() => {
    fetchAssets();
    fetchPortfolio();
  }, [filters]);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (filters.type) params.type = filters.type;
      if (filters.status) params.status = filters.status;
      if (search) params.search = search;

      const response = await assetsAPI.list(params);
      setAssets(response.data);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
      setError('Unable to load assets. Please check your connection and try again.');
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const fetchPortfolio = async () => {
    try {
      const response = await portfolioAPI.get();
      setPortfolio(response.data);
    } catch (error) {
      console.error('Failed to fetch portfolio:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    if (key === 'clear') {
      setFilters({ type: '', status: '' });
      setSearch('');
    } else {
      setFilters((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handleSearch = () => {
    fetchAssets();
  };

  // Calculate metrics
  const totalAssets = assets.length;
  const availableAssets = assets.filter((a) => a.status === 'available').length;
  const totalShares = assets.reduce((sum, a) => sum + a.total_shares, 0);
  const availableShares = assets.reduce((sum, a) => sum + (a.total_shares - a.sold_shares), 0);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Header />

      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[hsl(var(--text-primary))] mb-2">
            Fleet Assets
          </h1>
          <p className="text-[hsl(var(--text-secondary))]">
            Browse and invest in company trucks and trailers
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          <MetricCard
            title="Total Assets"
            value={formatNumber(totalAssets)}
            subtitle="Trucks & Trailers"
            icon={Container}
            color="neutral"
          />
          <MetricCard
            title="Available Now"
            value={formatNumber(availableAssets)}
            subtitle="Ready for investment"
            icon={TrendingUp}
            color="gain"
          />
          <MetricCard
            title="Total Shares"
            value={formatNumber(totalShares)}
            subtitle="Across all assets"
            icon={DollarSign}
            color="primary"
          />
          <MetricCard
            title="Available Shares"
            value={formatNumber(availableShares)}
            subtitle="Ready to reserve"
            icon={Truck}
            color="primary"
          />
        </div>

        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <SearchBar value={search} onChange={setSearch} />
            </div>
            <button
              data-testid="search-button"
              onClick={handleSearch}
              className="px-6 py-3 bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--brand-primary-dark))] text-white font-semibold rounded-lg transition-colors duration-200"
            >
              Search
            </button>
          </div>
          <FilterControls filters={filters} onFilterChange={handleFilterChange} />
        </div>

        {/* Asset Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-700 font-medium mb-2">Something went wrong</p>
            <p className="text-red-600 text-sm mb-4">{error}</p>
            <button
              onClick={fetchAssets}
              className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : assets.length === 0 ? (
          <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-12 text-center">
            <p className="text-[hsl(var(--text-secondary))] text-lg">No assets found</p>
            <p className="text-[hsl(var(--text-tertiary))] text-sm mt-2">
              Try adjusting your filters or search query
            </p>
          </div>
        ) : (
          <div data-testid="assets-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {assets.map((asset) => {
              // Find user's shares count for this asset
              const ownedAsset = portfolio?.assets?.find(a => a.asset.asset_id === asset.asset_id);
              const userShares = ownedAsset ? ownedAsset.shares_owned : 0;
              // On sale shares would come from marketplace - for now use asset data if available
              const onSaleShares = asset.on_sale_shares || 0;
              
              return (
                <AssetCard 
                  key={asset.asset_id} 
                  asset={asset} 
                  userShares={userShares}
                  onSaleShares={onSaleShares}
                />
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}